"use client";

import { AlertDialog as AlertDialogPrimitive } from "@base-ui/react/alert-dialog";
import * as React from "react";
import { AncestorLayerContext } from "../../lib/backdrop-layer";
import { cva, type VariantProps } from "../../lib/cva";
import { cn } from "../../lib/utils";
import { useVisualViewportBottomInset } from "../../hooks/use-visual-viewport-inset";
import { useEnterOnMount } from "../../hooks/use-enter-on-mount";
import { ScrollArea } from "./scroll-area";
import type { Omittable } from "../../lib/props";

export function AlertDialog({
  open: openProp,
  ...props
}: AlertDialogPrimitive.Root.Props): React.ReactElement {
  // An alert mounted already open still enters — see useEnterOnMount.
  const open = useEnterOnMount(openProp);
  return <AlertDialogPrimitive.Root {...props} open={open} />;
}

export const AlertDialogPortal: typeof AlertDialogPrimitive.Portal =
  AlertDialogPrimitive.Portal;

export function AlertDialogTrigger(
  props: AlertDialogPrimitive.Trigger.Props,
): React.ReactElement {
  return (
    <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
  );
}

export function AlertDialogBackdrop({
  className,
  ...props
}: AlertDialogPrimitive.Backdrop.Props): React.ReactElement {
  // Base UI drops a nested layer's backdrop on the assumption an ancestor
  // dimmed the page. Every modal layer paints its own scrim instead: a
  // confirm raised over an open dialog dims that dialog, which stays legible
  // and steps back behind it. See AncestorLayerContext.
  return (
    <AlertDialogPrimitive.Backdrop
      forceRender
      className={cn(
        "fixed inset-0 z-50 bg-black/32 backdrop-blur-sm transition-all duration-base data-ending-style:opacity-0 data-starting-style:opacity-0",
        className,
      )}
      data-slot="alert-dialog-backdrop"
      {...props}
    />
  );
}

export function AlertDialogViewport({
  className,
  ...props
}: AlertDialogPrimitive.Viewport.Props): React.ReactElement {
  // Keyboard compensation: the layout viewport this `fixed inset-0` element
  // sizes against does not shrink for the software keyboard on iOS Safari
  // (or any `resizes-visual` browser); the measured overlap becomes bottom
  // padding so the bottom-stuck popup stays above the keyboard.
  const keyboardInset = useVisualViewportBottomInset();
  return (
    <AlertDialogPrimitive.Viewport
      className={cn(
        "fixed inset-0 z-50 grid grid-rows-[1fr_auto_3fr] justify-items-center p-4",
        className,
      )}
      data-slot="alert-dialog-viewport"
      style={keyboardInset > 0 ? { paddingBottom: keyboardInset } : undefined}
      {...props}
    />
  );
}

export function AlertDialogPopup({
  className,
  children,
  bottomStickOnMobile = true,
  disablePointerDismissal = false,
  portalProps,
  ...props
}: AlertDialogPrimitive.Popup.Props & Omittable<{
  bottomStickOnMobile?: boolean;
  /**
   * By default an outside click closes the dialog — closing is always the
   * cancel path, so a stray click costs one re-open. Base UI hard-omits
   * pointer dismissal from alert dialogs, so the outside area is a `Close`
   * layer of our own. Disable it for a popup carrying typed input, where an
   * accidental dismissal eats the user's work.
   */
  disablePointerDismissal?: boolean;
  portalProps?: AlertDialogPrimitive.Portal.Props;
}>): React.ReactElement {
  return (
    <AlertDialogPortal {...portalProps}>
      <AlertDialogBackdrop />
      {/* A modal layer: a drawer opened from inside paints its own scrim over
          this popup (only a drawer-on-drawer stack withholds one). */}
      <AncestorLayerContext.Provider value="modal">
        <AlertDialogViewport
          className={cn(
            bottomStickOnMobile &&
              "max-sm:grid-rows-[1fr_auto] max-sm:p-0 max-sm:pt-12",
          )}
        >
          {!disablePointerDismissal && (
            // Click-catcher for the outside area: everything in the viewport
            // that is not the popup. The popup is a later positioned sibling,
            // so it paints above; portaled layers (nested pickers) mount after
            // both. Hidden from the a11y tree — Escape and the footer's Cancel
            // are the accessible dismiss paths.
            <AlertDialogPrimitive.Close
              aria-hidden
              className="absolute inset-0"
              nativeButton={false}
              render={<div />}
              tabIndex={-1}
            />
          )}
          <AlertDialogPrimitive.Popup
            className={cn(
              // `elevated`, not `popover` — an alert is a room like Dialog,
              // and the two must recede onto the same fill. See the fill note
              // on `dialogPopupVariants`.
              "relative row-start-2 flex max-h-full min-h-0 w-full min-w-0 max-w-lg origin-center flex-col overflow-clip rounded-xl border bg-elevated not-dark:bg-clip-padding text-foreground opacity-[calc(1-0.1*var(--nested-dialogs))] shadow-popover outline-none transition-[scale,opacity,translate] duration-base ease-in-out will-change-transform before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-xl)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] data-ending-style:opacity-0 data-starting-style:opacity-0 sm:scale-[calc(1-0.1*var(--nested-dialogs))] sm:data-ending-style:scale-98 sm:data-starting-style:scale-98 dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
              bottomStickOnMobile &&
                // The bottom-stuck popup meets the screen edge, so it owes the
                // home-indicator safe area its own padding.
                "max-sm:max-w-none max-sm:origin-bottom max-sm:rounded-none max-sm:border-x-0 max-sm:border-t max-sm:border-b-0 max-sm:pb-[env(safe-area-inset-bottom,0px)] max-sm:data-ending-style:translate-y-4 max-sm:data-starting-style:translate-y-4 max-sm:before:hidden max-sm:before:rounded-none",
              className,
            )}
            data-slot="alert-dialog-popup"
            {...props}
          >
            {children}
          </AlertDialogPrimitive.Popup>
        </AlertDialogViewport>
      </AncestorLayerContext.Provider>
    </AlertDialogPortal>
  );
}

/**
 * The popup carries NO padding of its own — the gutter is distributed the way
 * `Dialog` distributes it: the header owns the top inset, the footer the
 * bottom, and `AlertDialogPanel` the body's inset. Body content placed
 * directly between header and footer renders flush against the footer's
 * hairline; ALWAYS wrap it in `AlertDialogPanel`.
 */
export function AlertDialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn(
        // Followed by a panel, the header hands the body its 16px approach
        // (12 here + the panel's 4) — the Dialog rhythm.
        "flex flex-col gap-2 p-6 text-center in-[[data-slot=alert-dialog-popup]:has([data-slot=alert-dialog-panel])]:pb-3 max-sm:pb-4 sm:text-left",
        className,
      )}
      data-slot="alert-dialog-header"
      {...props}
    />
  );
}

export const alertDialogPanelVariants = cva(
  // 24px on every side that meets the popup edge; the header above already
  // paid most of the approach, and a `bare` footer pays the gap below.
  "p-6 in-[[data-slot=alert-dialog-popup]:has([data-slot=alert-dialog-header])]:pt-1 in-[[data-slot=alert-dialog-popup]:has([data-slot=alert-dialog-footer]:not(.border-t))]:pb-1",
);

/**
 * The body slot — REQUIRED around any content between `AlertDialogHeader`
 * and `AlertDialogFooter`: a type-to-confirm field, an impact list, a
 * version picker. Owns the body's inset and its scrolling, so a tall body
 * scrolls inside the popup instead of being clipped by it.
 */
export function AlertDialogPanel({
  className,
  scrollFade = true,
  ...props
}: React.ComponentProps<"div"> & Omittable<{
  scrollFade?: boolean;
}>): React.ReactElement {
  return (
    <ScrollArea scrollFade={scrollFade}>
      <div
        className={cn(alertDialogPanelVariants(), className)}
        data-slot="alert-dialog-panel"
        {...props}
      />
    </ScrollArea>
  );
}

export const alertDialogFooterVariants = cva(
  "flex flex-col-reverse gap-2 px-6 sm:flex-row sm:justify-end sm:rounded-b-[calc(var(--radius-xl)-1px)]",
  {
    defaultVariants: {
      variant: "default",
    },
    variants: {
      variant: {
        // The tinted band is the coss default: the footer reads as the
        // popup's action tray, not more body.
        default: "border-t bg-muted/72 py-4",
        // Panel above: its 4px + this 12px = the same 16px the header hands
        // the panel. Header directly above: its 24px is the whole gap.
        bare: "in-[[data-slot=alert-dialog-popup]:has([data-slot=alert-dialog-panel])]:pt-3 pb-6",
      },
    },
  },
);

export function AlertDialogFooter({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & Omittable<{
  variant?: VariantProps<typeof alertDialogFooterVariants>["variant"];
}>): React.ReactElement {
  return (
    <div
      className={cn(alertDialogFooterVariants({ variant }), className)}
      data-slot="alert-dialog-footer"
      {...props}
    />
  );
}

export function AlertDialogTitle({
  className,
  ...props
}: AlertDialogPrimitive.Title.Props): React.ReactElement {
  return (
    <AlertDialogPrimitive.Title
      className={cn(
        "font-heading font-semibold text-xl leading-none",
        className,
      )}
      data-slot="alert-dialog-title"
      {...props}
    />
  );
}

export function AlertDialogDescription({
  className,
  ...props
}: AlertDialogPrimitive.Description.Props): React.ReactElement {
  return (
    <AlertDialogPrimitive.Description
      className={cn("text-muted-foreground text-sm", className)}
      data-slot="alert-dialog-description"
      {...props}
    />
  );
}

export function AlertDialogClose(
  props: AlertDialogPrimitive.Close.Props,
): React.ReactElement {
  return (
    <AlertDialogPrimitive.Close data-slot="alert-dialog-close" {...props} />
  );
}
